<template>
  <div class="view" v-if="canEdit" v-cloak>
    <EditCreateTitleBox :label="title" />
    <div v-if="isLoading" class="loader-container">
      <Loader :loading="isLoading" size="sm"></Loader>
    </div>
    <div class="contracts-purchase-sale-add-edit-container" v-else>
        <!-- Left Box -->
        <StepsPanelWrapper
          ref="stepsWrapper"
          :steps="steps"
          :current-step.sync="currentStep"
          :step-props-fn="getStepProps"
          @step-input="onStepInput"
          @validation-error="handleValidationError"
        />

        <!-- Right Box -->
        <ContractsPurchaseSaleRightPanel :value="formData" :saving="saving" @save="save" @back="gotoList" />
    </div>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import settings from "./partials/settings.vue";
import products from "./partials/products.vue";
import additionalData from "./partials/additionalData.vue";
import ContractsPurchaseSaleRightPanel from "./partials/ContractsPurchaseSaleRightPanel.vue";
import { canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import Forbidden from "@/pages/errors/Forbidden";
import EditCreateTitleBox from "@/components/Common/EditCreateTitleBox.vue";
import StepsPanelWrapper from "@/components/Common/StepsPanelWrapper.vue";
import { successMessage, errorMessage, handlePromiseException } from "@/helpers/common";
import Loader from "@/components/Loader";
import moment from "moment";

export default {
  name: "ContractsPurchaseSaleAddOrEdit",
  data() {
    return {
      title: this.$t("stock_management.contracts_purchase_sale.edit"),
      isLoading: false,
      formData: {
        uuid: null,
        contract_type_id: null,
        contract_type: "",
        legal_entity_uuid: null,
        legal_entity_name: "",
        contract_number: "",
        partner_uuid: null,
        partner_name: "",
        sale_type: null,
        intragroup_legal_entity_uuid: null,
        intragroup_legal_entity_name: null,
        date: null,
        delivery_date: null,
        products: [],
        //additional data
        notes: null,
        contract_details: null,
        overwrite_delivery_note_quantity: 0,
      },
      steps: [
        {
          number: 1,
          component: settings,
          label: this.$t("common.settings"),
          ref: "ContractsPurchaseSaleSettings",
        },
        {
          number: 2,
          component: products,
          label: this.$t("common.product"),
          ref: "ContractsPurchaseSaleProducts",
        },
        {
          number: 3,
          component: additionalData,
          label: this.$t("common.additional_data"),
          ref: "ContractsPurchaseSaleAdditionalData",
        },
      ],
      currentStep: 1, // Set the current active step
      saving: false,
    };
  },
  computed: {
    ...mapGetters({
      quantityDecimalPlaces: "settings/getQuantityDecimalPlaces",
      getDefaultDateFormat: "settings/getDefaultDateFormat",
    }),
    config() {
      return {
        permission: permissions.SM_CONTRACTS_PURCHASE_SALE,
        permission_type: permissionTypes.access,
      };
    },
    canEdit() {
      return canEdit(this);
    },
  },
  components: {
    settings,
    products,
    additionalData,
    ContractsPurchaseSaleRightPanel,
    Forbidden,
    EditCreateTitleBox,
    StepsPanelWrapper,
    Loader,
  },
  methods: {
    close() {},
    ...mapActions({
      createSmContractsPurchaseSale: "smContractsPurchaseSale/createSmContractsPurchaseSale",
      updateSmContractsPurchaseSale: "smContractsPurchaseSale/updateSmContractsPurchaseSale",
      getSmContractsPurchaseSalesByUuid: "smContractsPurchaseSale/getSmContractsPurchaseSalesByUuid",
    }),
    handleAddSave(dataToSave) {
      this.createSmContractsPurchaseSale(dataToSave)
        .then((response) => {
          successMessage(this, response.message);
          this.$router.push({ name: "contracts-purchase-sale" });
        })
        .catch((e) => {
          handlePromiseException(this, e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    handleEditSave(dataToSave) {
      this.updateSmContractsPurchaseSale(dataToSave)
        .then((response) => {
          successMessage(this, response.message);
          this.$router.push({ name: "contracts-purchase-sale" });
        })
        .catch((e) => {
          handlePromiseException(this, e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async fetchSmContractsPurchaseSaleData(uuid) {
      try {
        this.getSmContractsPurchaseSalesByUuid({
          uuid: uuid,
        }).then((response) => {
          if (response && response.data) {
            response.data.date = moment(response.data.date).format(this.getDefaultDateFormat); // Format the date to the default format
            // Only format delivery_date if it's not null and not 'eet'
            if (response.data.delivery_date && response.data.delivery_date !== "eet") {
              response.data.delivery_date = moment(response.data.delivery_date).format(this.getDefaultDateFormat);
            }

            this.formData = { ...this.formData, ...response.data };

            for (const step of this.steps) {
              const stepComponent = this.$refs?.[step.ref];
              if (stepComponent && typeof stepComponent.updateData === "function") {
                if (step.number === 1) {
                  stepComponent.updateData(response.data);
                } else {
                  setTimeout(() => {
                    stepComponent.updateData(response.data);
                  }, step.number * 10);
                }
              }
            }
          }
        });
      } finally {
        this.isLoading = false;
      }
    },
    getStepProps() {
      return {
        formData: this.formData,
      };
    },
    onStepInput(step, payload) {
      if (!step) return;
      this.formData = { ...this.formData, ...(payload || {}) };
    },
    handleValidationError(error) {
      errorMessage(this, error.message || this.$t("fill_out_error"));
    },
    save() {
      this.validateStepsAndSave();
    },
    async validateStepsAndSave() {
      const wrapper = this.$refs?.stepsWrapper;
      if (wrapper && typeof wrapper.validateSteps === "function") {
        const errors = await Promise.resolve(wrapper.validateSteps());
        if (errors && errors.length) {
          errors.forEach((msg) => errorMessage(this, msg));
          return;
        }
      }

      this.saving = true;
      const dataToSave = {
        ...this.formData,
      };

      if (dataToSave.uuid) {
        // in edit mode do not change field report status
        this.handleEditSave(dataToSave);
      } else {
        this.handleAddSave(dataToSave);
      }
    },
    gotoList() {
      this.$router.push({ name: "contracts-purchase-sale" });
    },
    roundToDecimals(value, decimals) {
      if (typeof value !== "number") value = Number(value) || 0;
      return Number(value.toFixed(decimals));
    },
  },
  mounted() {
    if (this.$route.params.uuid && this.$route.params.uuid !== "add") {
      this.formData.uuid = this.$route.params.uuid;
      this.title = this.$t("stock_management.contracts_purchase_sale.edit");
      this.isLoading = true;
      setTimeout(() => {
        this.fetchSmContractsPurchaseSaleData(this.$route.params.uuid);
      }, 500);
    } else {
      this.formData.uuid = null;
      this.title = this.$t("stock_management.contracts_purchase_sale.create_new");
    }
  },
};
</script>

<style scoped>
.view {
  height: calc(100% - 70px);
}

.contracts-purchase-sale-add-edit-container {
  display: flex;
  height: 100%;
  width: 100%;
}
</style>

<style>
.contracts-purchase-sale-add-edit-container .dx-selectbox {
  margin-top: 8px !important;
}

.contracts-purchase-sale-add-edit-container .dx-textbox {
  margin-top: 8px !important;
}
</style>
